import type { SettingsInput, SettingsOperation } from '@veem/crm-schema'
import { showToast } from '@veem/ui'
import { createSignal, untrack } from 'solid-js'
import type { Snapshot } from '../../app.tsx'
import { api, reportRefreshFailure } from '../../lib/api.ts'
import { copySnapshot } from '../../lib/snapshot-value.ts'
import { hydrateData } from '../../prototype/data/hydration.ts'
import { proto } from '../../prototype/state/store.ts'
import { settingsSuccess } from './feedback.ts'

const [revision, setRevision] = createSignal<number | null>(null)
export const settingsRevision = revision
export const [settingsSaving, setSettingsSaving] = createSignal(false)
export const [settingsFailure, setSettingsFailure] = createSignal<string | null>(null)

export const createSettingsHandler =
  <Args extends unknown[]>(handler: (...args: Args) => Promise<unknown>) =>
  async (...args: Args) => {
    setSettingsFailure(null)
    try {
      const result = await handler(...args)
      return untrack(settingsFailure) ? false : result
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Не вдалося зберегти зміни'
      setSettingsFailure(message)
      showToast(message, 'error')
      return false
    }
  }

export const acceptSettingsRevision = (version: number) => {
  if (revision() === null || version >= (revision() ?? 0)) setRevision(version)
}

const run = async <K extends SettingsOperation>(
  operation: K,
  input: Omit<SettingsInput<K>, 'version'>,
  expectedVersion?: number | null,
) => {
  if (settingsSaving()) return null
  setSettingsSaving(true)
  setSettingsFailure(null)
  try {
    let version = expectedVersion ?? revision()
    if (version === null) {
      const current = await api<{ _version: number }>('/settings')
      version = current._version
      acceptSettingsRevision(version)
    }
    const result = await api<{ version: number; id?: string }>(
      `/settings/${operation.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}`,
      {
        method: 'POST',
        body: JSON.stringify({ ...input, version }),
      },
    )
    acceptSettingsRevision(result.version)
    try {
      const snapshot = copySnapshot(await api<Snapshot>('/snapshot'))
      hydrateData(snapshot.data)
      proto.hydrate(snapshot.state)
    } catch {
      reportRefreshFailure()
    }
    const success = settingsSuccess(operation, input)
    if (success) showToast(success)
    return result
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Не вдалося зберегти зміни'
    setSettingsFailure(message)
    showToast(message, 'error')
    const latest = await api<{ _version: number }>('/settings').catch(() => null)
    if (latest) acceptSettingsRevision(latest._version)
    return null
  } finally {
    setSettingsSaving(false)
  }
}

/** Uploads a letterhead image. The settings revision guards it like any other settings write. */
const uploadCompanyFile = async (id: string, kind: 'logo' | 'signature' | 'stamp', file: File) => {
  if (settingsSaving()) return null
  setSettingsSaving(true)
  setSettingsFailure(null)
  try {
    let version = revision()
    if (version === null) {
      version = (await api<{ _version: number }>('/settings'))._version
      acceptSettingsRevision(version)
    }
    const body = new FormData()
    body.set('id', id)
    body.set('kind', kind)
    body.set('version', String(version))
    body.set('file', file)
    let response: Response
    try {
      response = await fetch('/api/settings/set-company-file', {
        method: 'POST',
        credentials: 'include',
        body,
      })
    } catch {
      throw new Error('Немає зв’язку із сервером. Спробуйте ще раз.')
    }
    const result = (await response.json().catch(() => null)) as {
      ok: boolean
      data?: { version: number }
      error?: { message: string }
    } | null
    if (!response.ok || !result?.ok || !result.data)
      throw new Error(result?.error?.message ?? 'Не вдалося завантажити зображення')
    acceptSettingsRevision(result.data.version)
    try {
      const snapshot = copySnapshot(await api<Snapshot>('/snapshot'))
      hydrateData(snapshot.data)
      proto.hydrate(snapshot.state)
    } catch {
      reportRefreshFailure()
    }
    showToast('Зображення збережено')
    return result.data
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Не вдалося зберегти зміни'
    setSettingsFailure(message)
    showToast(message, 'error')
    const latest = await api<{ _version: number }>('/settings').catch(() => null)
    if (latest) acceptSettingsRevision(latest._version)
    return null
  } finally {
    setSettingsSaving(false)
  }
}

export const settingsActions = {
  run,
  uploadCompanyFile,
  renameWorkCenterInRecords: (from: string, to: string) => run('renameWorkCenter', { from, to }),
}
